import React, { useEffect, useMemo, useState } from "react";
import { Megaphone, Pin, MessageSquare, Send, LifeBuoy, Trash2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import EcosystemRoleDiagram from "@/components/dashboard/EcosystemRoleDiagram";

const ROLE_OPTIONS = [
  { key: "all", label: "Everyone" },
  { key: "affiliate", label: "Affiliates" },
  { key: "driver", label: "Drivers" },
  { key: "partner", label: "Partners" },
  { key: "franchisee", label: "Franchisees" },
  { key: "customer", label: "Customers" },
  { key: "admin", label: "Admins" },
];

export default function LandingPad() {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const isAdmin = useMemo(
    () => (user?.roles || []).some((r) => r === "admin" || r === "master_admin"),
    [user]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/landing-pad/feed");
        if (alive) setFeed(data.announcements || []);
      } catch (e) {
        if (alive) setErr(apiErrorMessage(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshKey]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-lifted-dim" data-testid="landing-pad-unauth">
        Sign in to access the Landing Pad.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-8" data-testid="landing-pad-page">
      <header>
        <div className="eyebrow">Lifted Landing Pad</div>
        <h1 className="font-anton text-4xl sm:text-5xl uppercase mt-2">
          Team communication
        </h1>
        <p className="text-lifted-dim mt-3 max-w-2xl">
          Announcements, role-targeted updates, and support requests across the
          ecosystem. {isAdmin ? "You can post to specific roles." : "Reply on threads or send a support request."}
        </p>
      </header>

      {/* Phase 16 — Interactive ecosystem role diagram */}
      <EcosystemRoleDiagram me={user} includeAdmin />

      {isAdmin && (
        <ComposeAnnouncement
          onPosted={() => setRefreshKey((k) => k + 1)}
        />
      )}

      <ComposeSupportRequest onPosted={() => setRefreshKey((k) => k + 1)} />

      {err && (
        <div className="glass-panel p-4 text-red-200" data-testid="landing-pad-error">
          {err}
        </div>
      )}

      {loading ? (
        <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING…</div>
      ) : feed.length === 0 ? (
        <div className="glass-panel p-10 text-center text-lifted-dim">
          No announcements in your feed yet.
        </div>
      ) : (
        <div className="space-y-4">
          {feed.map((a) => (
            <AnnouncementCard
              key={a.id}
              announcement={a}
              isAdmin={isAdmin}
              onChanged={() => setRefreshKey((k) => k + 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ComposeAnnouncement({ onPosted }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState(["all"]);
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function toggle(k) {
    setAudience((prev) => {
      if (k === "all") return ["all"];
      const without = prev.filter((p) => p !== "all" && p !== k);
      return prev.includes(k) ? without : [...without, k];
    });
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/landing-pad/announcements", {
        title,
        body,
        audience: audience.length ? audience : ["all"],
        pinned,
      });
      setTitle("");
      setBody("");
      setAudience(["all"]);
      setPinned(false);
      onPosted?.();
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="glass-panel p-5 space-y-3"
      data-testid="lp-compose-announcement"
    >
      <div className="flex items-center gap-2">
        <Megaphone size={16} className="text-lifted-green" />
        <div className="font-bebas tracking-[0.2em] uppercase text-sm">
          Broadcast announcement
        </div>
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white"
        required
        maxLength={200}
        data-testid="lp-announcement-title"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Message body…"
        rows={3}
        className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white"
        required
        maxLength={8000}
        data-testid="lp-announcement-body"
      />
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[10px] uppercase tracking-wider text-lifted-dim">
          Audience:
        </span>
        {ROLE_OPTIONS.map((r) => (
          <button
            type="button"
            key={r.key}
            onClick={() => toggle(r.key)}
            className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded border ${
              audience.includes(r.key)
                ? "bg-lifted-green/20 text-lifted-green border-lifted-green/50"
                : "border-white/10 text-lifted-dim hover:text-white"
            }`}
            data-testid={`lp-audience-${r.key}`}
          >
            {r.label}
          </button>
        ))}
        <label className="ml-auto inline-flex items-center gap-2 text-xs text-lifted-dim">
          <input
            type="checkbox"
            checked={pinned}
            onChange={(e) => setPinned(e.target.checked)}
            data-testid="lp-announcement-pinned"
          />
          <Pin size={12} /> Pinned
        </label>
      </div>
      {err && <div className="text-red-200 text-xs">{err}</div>}
      <button
        type="submit"
        disabled={busy}
        className="px-4 py-2 text-xs uppercase tracking-wider bg-lifted-green text-black rounded font-bebas hover:bg-lifted-green/90 disabled:opacity-50"
        data-testid="lp-announcement-submit"
      >
        <Send size={12} className="inline-block mr-1" />
        {busy ? "Posting…" : "Post announcement"}
      </button>
    </form>
  );
}

function ComposeSupportRequest({ onPosted }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/landing-pad/support-requests", { subject, body });
      setSubject("");
      setBody("");
      setOpen(false);
      onPosted?.();
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider border border-white/15 rounded text-white hover:bg-white/5"
        onClick={() => setOpen(true)}
        data-testid="lp-open-support"
      >
        <LifeBuoy size={14} /> Send support request
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="glass-panel p-5 space-y-3"
      data-testid="lp-compose-support"
    >
      <div className="flex items-center gap-2">
        <LifeBuoy size={16} className="text-lifted-green" />
        <div className="font-bebas tracking-[0.2em] uppercase text-sm">
          Support request (admins only see)
        </div>
      </div>
      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject"
        className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white"
        required
        maxLength={200}
        data-testid="lp-support-subject"
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="How can the team help?"
        rows={3}
        className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white"
        required
        maxLength={4000}
        data-testid="lp-support-body"
      />
      {err && <div className="text-red-200 text-xs">{err}</div>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="px-4 py-2 text-xs uppercase tracking-wider bg-lifted-green text-black rounded font-bebas hover:bg-lifted-green/90 disabled:opacity-50"
          data-testid="lp-support-submit"
        >
          {busy ? "Sending…" : "Send"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="px-4 py-2 text-xs uppercase tracking-wider border border-white/10 rounded text-white"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function AnnouncementCard({ announcement, isAdmin, onChanged }) {
  const [replies, setReplies] = useState(null);
  const [open, setOpen] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadReplies() {
    const { data } = await api.get(`/landing-pad/announcements/${announcement.id}`);
    setReplies(data.replies || []);
  }

  async function postReply(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/landing-pad/announcements/${announcement.id}/replies`, {
        body: replyBody,
      });
      setReplyBody("");
      await loadReplies();
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/landing-pad/announcements/${announcement.id}`);
    onChanged?.();
  }

  return (
    <article
      className="glass-panel p-5"
      data-testid={`lp-announcement-${announcement.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            {announcement.pinned && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-200">
                <Pin size={10} /> Pinned
              </span>
            )}
            {announcement.type === "support_request" && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-400/30 bg-amber-400/10 text-amber-100">
                Support
              </span>
            )}
            <span className="text-[10px] uppercase tracking-wider text-lifted-dim">
              {announcement.author_name} ·{" "}
              {new Date(announcement.created_at).toLocaleString()}
            </span>
          </div>
          <h3 className="font-anton text-xl text-white">{announcement.title}</h3>
          <p className="text-sm text-lifted-text/90 mt-1 whitespace-pre-wrap">
            {announcement.body}
          </p>
          <div className="mt-2 flex gap-1 flex-wrap">
            {(announcement.audience || []).map((a) => (
              <span
                key={a}
                className="text-[10px] uppercase tracking-wider text-lifted-dim border border-white/10 rounded px-1.5"
              >
                {a}
              </span>
            ))}
          </div>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={remove}
            className="text-lifted-dim hover:text-red-300"
            title="Delete"
            data-testid={`lp-delete-${announcement.id}`}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      <button
        type="button"
        className="mt-3 text-xs inline-flex items-center gap-1 text-lifted-dim hover:text-lifted-green"
        onClick={async () => {
          if (!open) await loadReplies();
          setOpen((o) => !o);
        }}
        data-testid={`lp-toggle-replies-${announcement.id}`}
      >
        <MessageSquare size={12} /> {announcement.reply_count || 0} replies
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          {(replies || []).map((r) => (
            <div
              key={r.id}
              className="border-l border-lifted-green/30 pl-3 text-sm"
              data-testid={`lp-reply-${r.id}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-lifted-dim">
                {r.author_name} · {new Date(r.created_at).toLocaleString()}
              </div>
              <div className="text-white/90 whitespace-pre-wrap">{r.body}</div>
            </div>
          ))}
          <form onSubmit={postReply} className="flex gap-2 pt-2">
            <input
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              placeholder="Reply…"
              className="flex-1 bg-black/40 border border-white/10 rounded px-3 py-1.5 text-white text-sm"
              required
              maxLength={4000}
              data-testid={`lp-reply-input-${announcement.id}`}
            />
            <button
              type="submit"
              disabled={busy}
              className="px-3 py-1.5 text-xs uppercase tracking-wider bg-lifted-green text-black rounded font-bebas hover:bg-lifted-green/90 disabled:opacity-50"
              data-testid={`lp-reply-submit-${announcement.id}`}
            >
              {busy ? "…" : "Reply"}
            </button>
          </form>
        </div>
      )}
    </article>
  );
}
